<template>
    <b-navbar :disabled="notLoaded" class="bg-light drop-shadow toolbar p-2">
        <b-modal id="helpModal" hide-footer title="" size="lg">
            <div class="p-1">
                <p class="lead">CoPHI is an aggregated <a href="https://en.wikipedia.org/wiki/Parallel_coordinates">parallel
                    coordinate</a> tool made to explore (numerical) multidimensional
                    data.</p>
                <h3>Presentation </h3>
                <p>Multidimensional items are grouped along each dimension forming <em>nodes</em> on each axis and
                    <em>links</em> between consecutive axes. </p>
                <h3>Toolbar</h3>
                <dl class="row">
                    <dt class="col-sm-3">
                        <icon class="small-icon" filename="pan"></icon>
                        Select <kbd>Space</kbd></dt>
                    <dd class="col-sm-9">Click on <em>link</em> and <em>node</em> to highlight elements. Hold
                        <kbd>Shift</kbd>
                        to merge (set union) selections, <kbd>Ctrl</kbd> to substract (set difference) selections and
                        <kbd>Alt</kbd>
                        to obtain the intersection of two selections
                    </dd>
                    <dt class="col-sm-3">
                        <icon class="small-icon" filename="filter"></icon>
                        Axis filter <kbd>Space</kbd></dt>
                    <dd class="col-sm-9">Drag sliding arrows up and down each axis to highlight items only within the
                        corresponding dimension range. Axis constraints are combined with a logical and.
                    </dd>
                </dl>
                <!--            <h3>Importing your own data</h3>-->
                <!--            <p>TODO</p>-->
                <h3>About</h3>
                <p>Find the project <a href="http://www.labri.fr/perso/jsansen/CoPHI.html">webpage here</a>. To cite
                    CoPHI,
                    please refer to the original article:</p>
                <blockquote>Sansen, J.; Richer, G.; Jourde, T.; Lalanne, F.; Auber, D.; Bourqui, R. "Visual Exploration
                    of
                    Large Multidimensional Data
                    Using Parallel Coordinates on Big Data Infrastructure". <em>Informatics 2017</em>, 4, 21.
                </blockquote>
                <div>
                    <b-button class="ml-2" variant="outline-primary" href="http://www.mdpi.com/2227-9709/4/3/21">
                        <font-awesome-icon icon="link"></font-awesome-icon>
                        Paper
                    </b-button>
                    <b-button class="ml-2" variant="outline-primary" :click="copyBibToClipboard">
                        <font-awesome-icon icon="code"></font-awesome-icon>
                        Copy BibTex
                    </b-button>
                </div>
            </div>
        </b-modal>

        <b-nav>
            <b-button v-for="mode in Object.keys(modes)" :key="mode"
                      :disabled="notLoaded" @click="onModeChange(mode)" variant="link"
                      :pressed.sync=modes[mode].pressed v-b-tooltip.hover :title=modes[mode].tooltip>
                <Icon :filename=modes[mode].icon></Icon>
            </b-button>
        </b-nav>
        <b-nav style="float: right">
            <b-button :disabled="notLoaded" variant="link" v-b-tooltip.hover title="Center" @click="center">
                <Icon filename="center"></Icon>
            </b-button>
            <b-button :disabled="notLoaded" variant="link" v-b-tooltip.hover title="Fit" @click="fit">
                <Icon filename="fit"></Icon>
            </b-button>
            <b-button :disabled="notLoaded" :pressed.sync=lockPan variant="link"
                      v-b-tooltip.hover :title="(lockPan?'Unlock':'Lock')+' pan'"
                      @click=lockNavigation>
                <Icon filename="lockpan"></Icon>
            </b-button>
            <b-button :disabled="notLoaded" :pressed.sync=lockZoom variant="link"
                      v-b-tooltip.hover :title="(lockPan?'Unlock':'Lock')+' zoom'"
                      @click=lockNavigation>
                <Icon filename="lockzoom"></Icon>
            </b-button>
            <b-button variant="link" v-b-tooltip.hover title="Help" v-b-modal.helpModal>
                <Icon filename="help"></Icon>
            </b-button>
        </b-nav>
    </b-navbar>
</template>

<script>
    import Icon from "./Icon";
    import copy from 'copy-to-clipboard';
    import bibentry from '!raw-loader!@/assets/cophi-paper.bib';
    import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";


    export default {
        name: 'toolbar',
        components: {
            Icon,
            FontAwesomeIcon
        },
        data() {
            return {
                lockPan: false,
                lockZoom: false,
                selectedMode: 'clickSelection',
                modes: {
                    clickSelection: {tooltip: 'Select with click', icon: 'pan', pressed: true},
                    sliderSelection: {tooltip: 'Select with dimension range', icon: 'filter', pressed: false},
                    variationSelection: {
                        tooltip: 'Select based on amount of variations',
                        icon: 'variation',
                        pressed: false
                    },
                    rescale: {tooltip: 'Resize visualization', icon: 'scale', pressed: false}, // TODO: add back these modes
                    // deleteAxes: {tooltip: 'Delete axes', icon: 'delete', pressed: false},
                    // insertAxes: {tooltip: 'Insert deleted dimensions', icon: 'insert', pressed: false},
                    // moveAxes: {tooltip: 'Move axes', icon: 'move', pressed: false},
                    invertAxis: {tooltip: 'Flip axes', icon: 'invert', pressed: false},
                    // picker: {tooltip: 'View element info', icon: 'info', pressed: false}
                },
                actions: {}
            };
        },
        computed: {
            notLoaded() {
                return this.$store.getters.loadingProgress !== 100;
            }
        },
        methods: {
            onModeChange(name) {
                this.modes[this.selectedMode].pressed = false;
                this.selectedMode = name;
                this.modes[name].pressed = true;
                this.$store.commit('changeTool', name);
            },
            lockNavigation() {
                this.$store.commit('updateSettings', {activatePan: !this.lockPan, activateZoom: !this.lockZoom});
            },
            copyBibToClipboard() {
                copy(bibentry)
            },
            fit() {
                this.$store.commit('fit')
            },
            center() {
                this.$store.commit('center')
            }
        }
    };
</script>
<style scoped lang="less">
    @import "../variables.less";
    @import "../mixins.less";

    .navbar {
        .shadows(@deeper-color);

        button, .btn {
            color: @deep-color;
            border-width: 0 0 0 0;
        }

        button.active, button.focus {
            color: @accent-color;
            border-radius: 0;
            border: 0 solid @accent-color;
            border-bottom-width: 6px;
        }

        button:hover {
            color: @light-color;
            border-radius: 0;
            border: 0 solid @accent-color;
            border-bottom-width: 6px;
        }
    }

    blockquote {
        background: #f9f9f9;
        border-left: 10px solid #ccc;
        margin: 1.5em 10px;
        padding: 0.5em 10px;
    }

    blockquote:before {
        color: #ccc;
        content: open-quote;
        font-size: 4em;
        line-height: 0.1em;
        margin-right: 0.25em;
        vertical-align: -0.4em;
    }

    blockquote p {
        display: inline;
    }

</style>
